import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { firestore, functions } from "../firebase";
import useAuth from "../hooks/useAuth";
import "../styles/quotation-system.css";

export default function OAuthCallback() {
  const history = useHistory();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Extract query parameters
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        // Check for errors from OAuth provider
        if (errorParam) {
          setError(errorDescription || errorParam || "Authorization failed");
          setStatus("error");
          setTimeout(() => {
            history.push("/tracking");
          }, 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError("Missing authorization code or state parameter");
          setStatus("error");
          setTimeout(() => {
            history.push("/tracking");
          }, 3000);
          return;
        }

        // Parse state to determine provider and customer ID
        let stateData;
        try {
          stateData = JSON.parse(decodeURIComponent(state));
        } catch (e) {
          setError("Invalid state parameter");
          setStatus("error");
          setTimeout(() => {
            history.push("/tracking");
          }, 3000);
          return;
        }

        const { provider: stateProvider, customerId } = stateData;
        setProvider(stateProvider);

        if (!stateProvider || !customerId) {
          setError("Invalid state data");
          setStatus("error");
          setTimeout(() => {
            history.push("/tracking");
          }, 3000);
          return;
        }

        // Verify user is authenticated and is the customer
        if (!user || !profile) {
          setError("Please log in first");
          setStatus("error");
          setTimeout(() => {
            history.push("/login");
          }, 2000);
          return;
        }

        if (profile.role !== "customer" || user.uid !== customerId) {
          setError("Unauthorized access");
          setStatus("error");
          setTimeout(() => {
            history.push("/tracking");
          }, 3000);
          return;
        }

        setStatus("exchanging");

        // Exchange authorization code for tokens
        const exchangeToken = functions.httpsCallable("exchangeOAuthToken");
        const result = await exchangeToken({
          provider: stateProvider,
          code,
          state,
          customerId: user.uid
        });

        if (result.data.success) {
          setStatus("success");
          
          // Update customer record with OAuth status
          await firestore.collection("opexCustomers").doc(user.uid).update({
            [`${stateProvider}OAuthStatus`]: "active",
            [`${stateProvider}OAuthConnectedAt`]: new Date(),
            [`${stateProvider}LastTokenRefresh`]: new Date()
          });

          // Redirect to dashboard after short delay
          setTimeout(() => {
            history.push("/tracking");
          }, 2000);
        } else {
          setError(result.data.error || "Failed to complete authorization");
          setStatus("error");
          setTimeout(() => {
            history.push("/tracking");
          }, 3000);
        }
      } catch (err) {
        console.error("OAuth callback error:", err);
        setError(err.message || "An error occurred during authorization");
        setStatus("error");
        setTimeout(() => {
          history.push("/tracking");
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [location.search, history, user, profile]);

  const getProviderName = () => {
    if (provider === "solis") return "Solis";
    if (provider === "sungrow") return "Sungrow";
    return "Solar Inverter";
  };

  return (
    <div className="quotation-system" style={{ 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      minHeight: "100vh",
      padding: "2rem"
    }}>
      <div className="elegant-card fade-in-up" style={{ 
        maxWidth: "500px", 
        width: "100%",
        textAlign: "center"
      }}>
        {status === "processing" && (
          <>
            <div className="loading-elegant" style={{ margin: "2rem auto" }}></div>
            <h2 style={{ marginBottom: "1rem", color: "var(--primary-deep)" }}>
              Processing Authorization
            </h2>
            <p style={{ color: "var(--neutral-600)" }}>
              Please wait while we connect your {getProviderName()} account...
            </p>
          </>
        )}

        {status === "exchanging" && (
          <>
            <div className="loading-elegant" style={{ margin: "2rem auto" }}></div>
            <h2 style={{ marginBottom: "1rem", color: "var(--primary-deep)" }}>
              Completing Authorization
            </h2>
            <p style={{ color: "var(--neutral-600)" }}>
              Securing your connection to {getProviderName()}...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div style={{
              width: "64px",
              height: "64px",
              margin: "2rem auto",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.4)"
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={{ marginBottom: "1rem", color: "var(--primary-deep)" }}>
              Authorization Successful!
            </h2>
            <p style={{ color: "var(--neutral-600)", marginBottom: "1rem" }}>
              Your {getProviderName()} account has been connected successfully.
            </p>
            <p style={{ color: "var(--neutral-500)", fontSize: "0.875rem" }}>
              Redirecting to your dashboard...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div style={{
              width: "64px",
              height: "64px",
              margin: "2rem auto",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(239, 68, 68, 0.4)"
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={{ marginBottom: "1rem", color: "var(--primary-deep)" }}>
              Authorization Failed
            </h2>
            <p style={{ color: "var(--neutral-600)", marginBottom: "1rem" }}>
              {error || "An error occurred during the authorization process."}
            </p>
            <p style={{ color: "var(--neutral-500)", fontSize: "0.875rem" }}>
              Redirecting to your dashboard...
            </p>
          </>
        )}
      </div>
    </div>
  );
}

